import { centsToDollar } from '@utils/currency';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import type { FinancesEarningsTimeseriesBucket } from './apiHelpers';
import { getDateFilterLabel, type DateFilterOption } from './dateRange';
import { SummaryCardConfig, SummarySparkline } from './SummarySparkline';
import styles from './SummaryCards.scss';

export interface SummaryCardsProps {
  dateFilter: DateFilterOption;
  grossEarnings?: number;
  netEarnings?: number;
  refunds?: number;
  refundCount?: number;
  timeseriesBuckets?: FinancesEarningsTimeseriesBucket[];
}

const formatRefundsContext = (refundCount?: number): string => {
  if (!refundCount) {
    return '0 refunds';
  }

  return `${refundCount} refund${refundCount === 1 ? '' : 's'}`;
};

export const SummaryCards = ({
  dateFilter,
  grossEarnings,
  netEarnings,
  refunds,
  refundCount,
  timeseriesBuckets = [],
}: SummaryCardsProps) => {
  const sparklineValues = timeseriesBuckets.map(
    (bucket) => bucket.revenue - bucket.refunds,
  );

  const cards: SummaryCardConfig[] = [
    {
      label: 'Net Earnings',
      value: netEarnings,
      context: getDateFilterLabel(dateFilter),
      sparklineValues,
      showSparklineShadow: true,
    },
    {
      label: 'Gross Earnings',
      value: grossEarnings,
      context: 'Before fees & refunds',
      sparklineValues: timeseriesBuckets.map((bucket) => bucket.revenue),
    },
    {
      label: 'Refunds',
      value: refunds,
      context: formatRefundsContext(refundCount),
      sparklineValues: timeseriesBuckets.map((bucket) => bucket.refunds),
    },
  ];

  return (
    <div className={styles.grid}>
      {cards.map((card) => (
        <article className={styles.card} key={card.label}>
          <p className={cx(typography.c3, styles.label)}>{card.label}</p>
          <p className={cx(typography.h2, styles.value)}>
            {centsToDollar(card.value)}
          </p>
          <p className={cx(typography.c3, styles.context)}>
            {card.delta && <span className={styles.delta}>{card.delta}</span>}
            {card.context}
          </p>
          <SummarySparkline
            showShadow={card.showSparklineShadow}
            values={card.sparklineValues ?? []}
          />
        </article>
      ))}
    </div>
  );
};
